import { MF_EASINGS } from "./easings.js";

// src/core/engine.js
export function MotionFlowEngine(options = {}) {

  /* ==========================================================
     VIEWPORT PRE-CALC SETTINGS
  ========================================================== */
  const PRELOAD_MULTIPLIER = 1.2;

  /* ==========================================================
     PLUGIN DEFAULTS (ANIMATION)
  ========================================================== */
  const defaults = {
    once: false,
    distance: 100,
    duration: 600,
    delay: 0,
    easing: "ease",
    trigger: "top 90%",
    repeat: "both"
  };

  /* ==========================================================
     STAGGER DEFAULTS
  ========================================================== */
  const staggerDefaults = {
    delay: 0,
    gap: 100
  };

  /* ==========================================================
     INTERNAL STATE
  ========================================================== */
  let items = [];
  let ticking = false;
  let scrollHandler = null;
  let resizeHandler = null;
  let observer = null;
  let refreshTimer = null;

  /* ==========================================================
     HELPERS
  ========================================================== */
  const num = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  function parseTrigger(str) {
    if (!str) return { elementAnchor: "top", viewportAnchor: 70 };
    const parts = str.trim().toLowerCase().split(/\s+/);
    const map = { top: 0, center: 50, bottom: 100 };
    return {
      elementAnchor: parts[0] || "top",
      viewportAnchor: parts[1]?.endsWith("%")
        ? parseInt(parts[1])
        : map[parts[1]] ?? 70
    };
  }

  function getAnchor(rect, anchor) {
    if (anchor === "center") return rect.top + rect.height / 2;
    if (anchor === "bottom") return rect.bottom;
    return rect.top;
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(update, 60);
  }

  function startObserver() {
    if (observer) return;
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-mf-animation"]
    });
  }

  function stopObserver() {
    observer?.disconnect();
    observer = null;
    clearTimeout(refreshTimer);
  }

  /* ==========================================================
     WILL-CHANGE (JS CONTROLLED)
  ========================================================== */
  function applyWillChange(el) {
    el.style.willChange = "transform, opacity";

    const cleanup = () => {
      el.style.willChange = "";
      el.removeEventListener("animationend", cleanup);
    };

    el.addEventListener("animationend", cleanup);
  }

  /* ==========================================================
     UPDATE (SCROLL CHECK)
  ========================================================== */
  function update() {
    if (!items.length) return;

    const vh = window.innerHeight;
    const buffer = vh * PRELOAD_MULTIPLIER;

    items.forEach(item => {
      const el = item.el;
      if (!document.documentElement.contains(el)) return;

      const rect = (el.__mfStaggerParent || el).getBoundingClientRect();
      if (rect.top > vh + buffer || rect.bottom < -buffer) return;

      const pos = getAnchor(rect, item.trigger.elementAnchor);
      const vp = (vh * item.trigger.viewportAnchor) / 100;

      const visible = pos < vp && rect.bottom > 0;
      const above = rect.bottom <= 0;
      const below = rect.top >= vh;

      if (visible && !item.triggered) {
        item.triggered = true;
        applyWillChange(el);
        el.classList.add("mf-animate");
      }

      if (!item.once) {
        if (
          (item.repeat === "top" && above) ||
          (item.repeat === "bottom" && below) ||
          (item.repeat === "both" && (above || below))
        ) {
          item.triggered = false;
          el.classList.remove("mf-animate");
        }
      }
    });
  }

  /* ==========================================================
     INIT
  ========================================================== */
  function init() {
    items = [];
    ticking = false;

    const body = document.body;

    const animationOptions =
      options.animation === true ? {} : (options.animation || {});
    const staggerOptions = animationOptions.stagger || {};

    const settings = {
      once: animationOptions.once ?? defaults.once,
      distance: num(animationOptions.distance ?? defaults.distance),
      duration: num(animationOptions.duration ?? defaults.duration),
      delay: num(animationOptions.delay ?? defaults.delay),
      easing: animationOptions.easing ?? defaults.easing,
      trigger: animationOptions.trigger ?? defaults.trigger,
      repeat: animationOptions.repeat ?? defaults.repeat
    };

    body.style.setProperty("--mf-distance", settings.distance + "px");
    body.style.setProperty("--mf-duration", settings.duration + "ms");
    body.style.setProperty("--mf-delay", settings.delay + "ms");
    body.style.setProperty(
      "--mf-easing",
      MF_EASINGS[settings.easing] || settings.easing
    );

    /* ==========================================================
       🔴 STAGGER CLEANUP (IDEMPOTENT FIX)
       This is what makes stagger work inside document.ready
    ========================================================== */
    document.querySelectorAll("[data-mf-animation]").forEach(el => {
      if (!el.__mfStaggerParent) return;

      delete el.__mfStaggerParent;
      delete el.dataset.mfAnimation;
      delete el.dataset.mfAnimationDelay;
      delete el.dataset.mfAnimationDistance;
      delete el.dataset.mfAnimationDuration;
      delete el.dataset.mfAnimationEasing;
      delete el.dataset.mfAnimationOnce;
      delete el.dataset.mfAnimationRepeat;
    });

    /* ==========================================================
       STAGGER INJECTION
    ========================================================== */
    const staggerParents = new Map();

    document.querySelectorAll("[data-mf-stagger-animation]").forEach(parent => {
      const animation = parent.dataset.mfStaggerAnimation;

      const baseDelay =
        num(parent.dataset.mfStaggerDelay) ??
        num(staggerOptions.delay) ??
        settings.delay ??
        staggerDefaults.delay;

      const gap =
        num(parent.dataset.mfStaggerGap) ??
        num(staggerOptions.gap) ??
        staggerDefaults.gap;

      const distance =
        num(parent.dataset.mfStaggerDistance) ??
        num(staggerOptions.distance) ??
        settings.distance;

      const duration =
        num(parent.dataset.mfStaggerDuration) ??
        num(staggerOptions.duration) ??
        settings.duration;

      const easing =
        parent.dataset.mfStaggerEasing ??
        staggerOptions.easing ??
        settings.easing;

      const once =
        parent.dataset.mfStaggerOnce ??
        staggerOptions.once ??
        settings.once;

      const repeat =
        parent.dataset.mfStaggerRepeat ??
        staggerOptions.repeat ??
        settings.repeat;

      const trigger = parseTrigger(
        parent.dataset.mfStaggerTrigger ??
        staggerOptions.trigger ??
        settings.trigger
      );

      staggerParents.set(parent, trigger);

      Array.from(parent.children).forEach((child, index) => {
        if (child.hasAttribute("data-mf-stagger-ignore")) return;
        if (child.hasAttribute("data-mf-animation")) return;

        child.__mfStaggerParent = parent;
        child.dataset.mfAnimation = animation;
        child.dataset.mfAnimationDelay = baseDelay + index * gap;

        if (distance !== undefined)
          child.dataset.mfAnimationDistance = distance;

        if (duration !== undefined)
          child.dataset.mfAnimationDuration = duration;

        if (easing)
          child.dataset.mfAnimationEasing = easing;

        if (once !== undefined)
          child.dataset.mfAnimationOnce = once;

        if (repeat)
          child.dataset.mfAnimationRepeat = repeat;
      });
    });

    /* ==========================================================
       COLLECT ELEMENTS
    ========================================================== */
    document.querySelectorAll("[data-mf-animation]").forEach(el => {
      if (!el.dataset.mfAnimation) return;

      el.classList.add("mf-init");

      if (el.dataset.mfAnimationDistance)
        el.style.setProperty("--mf-distance", el.dataset.mfAnimationDistance + "px");

      if (el.dataset.mfAnimationDuration)
        el.style.setProperty("--mf-duration", el.dataset.mfAnimationDuration + "ms");

      if (el.dataset.mfAnimationDelay)
        el.style.setProperty("--mf-delay", el.dataset.mfAnimationDelay + "ms");

      if (el.dataset.mfAnimationEasing)
        el.style.setProperty(
          "--mf-easing",
          MF_EASINGS[el.dataset.mfAnimationEasing] || el.dataset.mfAnimationEasing
        );

      items.push({
        el,
        once:
          el.dataset.mfAnimationOnce !== undefined
            ? el.dataset.mfAnimationOnce === "true" || el.dataset.mfAnimationOnce === ""
            : settings.once,
        repeat:
          (el.dataset.mfAnimationRepeat ?? settings.repeat).toLowerCase(),
        trigger: el.__mfStaggerParent
          ? staggerParents.get(el.__mfStaggerParent)
          : parseTrigger(el.dataset.mfAnimationTrigger ?? settings.trigger),
        triggered: el.classList.contains("mf-animate")
      });
    });

    scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          update();
          ticking = false;
        });
        ticking = true;
      }
    };

    resizeHandler = update;

    update();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", resizeHandler);
    startObserver();
  }

  function refresh() {
    update();
  }

  function reinit() {
    destroy(false);
    init();
  }

  function destroy(removeStyles = true) {
    window.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", resizeHandler);
    stopObserver();

    if (removeStyles) {
      items.forEach(({ el }) => {
        el.classList.remove("mf-init", "mf-animate");
        el.style.removeProperty("--mf-distance");
        el.style.removeProperty("--mf-duration");
        el.style.removeProperty("--mf-delay");
        el.style.removeProperty("--mf-easing");
        el.style.willChange = "";
      });
    }

    items = [];
    ticking = false;
  }

  init();
  return { refresh, destroy, reinit };
}
